import { useMemo, useState } from 'react'
import { X, Loader2, Calculator, RotateCcw, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { postJson } from '../../lib/api.js'
import { fmtMonthTH } from '../../lib/timetable.js'
import { periodForMonth, ymFromEnd, isDefaultPeriod } from '../../lib/salary.js'
import EmployeePicker from './EmployeePicker.jsx'
import ConfirmDialog from '../ConfirmDialog.jsx'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

/**
 * คำนวณเงินเดือน (gen2). Defaults to the 26→25 window that produces the ledger
 * month being viewed, SSO 5%, and ALL employees. gen2 deletes only the
 * generate==true rows for the derived {year, month} and rebuilds them — manual
 * rows are safe — so re-running is fine.
 *
 * Guard: if the SSO rate is not 5 OR the period is not the 26→25 default, a
 * second confirmation is required before the run (both change the money).
 */
export default function GenerateModal({ year, month, onClose, onDone }) {
  const [period, setPeriod] = useState(() => periodForMonth(year, month))
  const [sso, setSso] = useState('5')
  const [employeeId, setEmployeeId] = useState('') // '' = ทั้งหมด
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(null) // { count }
  const [confirmWarn, setConfirmWarn] = useState(false)

  const derived = useMemo(() => ymFromEnd(period.end), [period.end])
  const ssoNum = Number(sso)
  const nonDefault = !isDefaultPeriod(period) || ssoNum !== 5

  function resetPeriod() { setPeriod(periodForMonth(year, month)); setSso('5') }

  function attempt() {
    setErr('')
    if (!period.start || !period.end) { setErr('กรุณาระบุช่วงวันที่'); return }
    if (period.end < period.start) { setErr('วันที่สิ้นสุดต้องอยู่หลังวันที่เริ่มต้น'); return }
    if (!Number.isFinite(ssoNum) || ssoNum < 0) { setErr('อัตราประกันสังคมไม่ถูกต้อง'); return }
    if (nonDefault) { setConfirmWarn(true); return }
    run()
  }

  async function run() {
    setConfirmWarn(false)
    setRunning(true); setErr('')
    try {
      const res = await postJson('/api/hr/salary-generate', {
        start_date: period.start,
        end_date: period.end,
        sso_percent: ssoNum,
        year: derived.year,
        month: derived.month,
        employee_id: employeeId ? Number(employeeId) : 0,
      })
      const count = Array.isArray(res) ? res.length : (Array.isArray(res?.items) ? res.items.length : null)
      setResult({ count })
    } catch (e) { setErr(String(e.message || e)) }
    finally { setRunning(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[480px] bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-ss-pop-in">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <Calculator size={17} className="text-brand-600" />
            <h2 className="text-[15.5px] font-semibold text-slate-900">คำนวณเงินเดือน</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        {result ? (
          <div className="px-5 py-8 text-center">
            <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
            <div className="text-[15px] font-semibold text-slate-800">คำนวณเงินเดือนเรียบร้อย</div>
            <p className="text-[13px] text-slate-500 mt-1">
              รอบ {fmtMonthTH(derived.year, derived.month)}
              {result.count != null ? ` · ประมวลผลพนักงาน ${result.count} คน` : ''}
            </p>
            <button onClick={() => onDone?.()}
              className="mt-5 px-5 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
              ดูรายการ
            </button>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className={LABEL}>ขอบเขต</label>
                <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll allLabel="พนักงานทั้งหมด" className="w-full" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={LABEL + ' mb-0'}>ช่วงวันที่ (รอบเงินเดือน)</label>
                  <button onClick={resetPeriod} className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-700">
                    <RotateCcw size={12} /> รอบปกติ 26–25
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} className={FIELD} />
                  <span className="text-slate-400 text-[13px]">ถึง</span>
                  <input type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} className={FIELD} />
                </div>
                <p className="text-[11.5px] text-slate-400 mt-1">
                  บันทึกเป็นรอบ <span className="font-medium text-slate-500">{fmtMonthTH(derived.year, derived.month)}</span> (ตามวันที่สิ้นสุด)
                </p>
              </div>

              <div>
                <label className={LABEL}>อัตราประกันสังคม (%)</label>
                <input type="number" min="0" step="0.5" value={sso} onChange={(e) => setSso(e.target.value)}
                  className={`${FIELD} max-w-[120px] tabular-nums`} />
                <p className="text-[11.5px] text-slate-400 mt-1">ปกติ 5% · ฐานเงินเดือนเกิน 17,500 ระบบจะจำกัดที่ 875 อัตโนมัติ</p>
              </div>

              <div className="flex items-start gap-2 rounded-xl bg-slate-50 ring-1 ring-slate-200 px-3 py-2.5 text-[12px] text-slate-500">
                <AlertTriangle size={14} className="mt-0.5 shrink-0 text-slate-400" />
                <span>ระบบจะลบเฉพาะรายการที่คำนวณไว้ของรอบนี้แล้วสร้างใหม่ · รายการที่เพิ่มเองจะไม่ถูกลบ</span>
              </div>

              {err && <div className="text-[12.5px] text-rose-500">{err}</div>}
            </div>

            <div className="border-t border-slate-100 px-5 py-3.5 flex items-center justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-xl text-[13.5px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
              <button onClick={attempt} disabled={running}
                className="px-4 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5">
                {running && <Loader2 size={14} className="animate-spin" />} คำนวณ
              </button>
            </div>
          </>
        )}
      </div>

      {confirmWarn && (
        <ConfirmDialog
          title="ยืนยันการคำนวณที่ต่างจากค่าปกติ"
          message={
            `คุณกำลังคำนวณด้วยค่าที่ไม่ใช่ค่าตั้งต้น` +
            (ssoNum !== 5 ? ` · ประกันสังคม ${sso}% (ปกติ 5%)` : '') +
            (!isDefaultPeriod(period) ? ` · ช่วงวันที่ ${period.start} ถึง ${period.end} (ไม่ใช่รอบ 26–25 ปกติ)` : '') +
            ` — ต้องการดำเนินการต่อหรือไม่?`
          }
          confirmLabel="คำนวณต่อ"
          tone="danger"
          onConfirm={run}
          onCancel={() => setConfirmWarn(false)}
        />
      )}
    </div>
  )
}
